import { Check, ListChecks } from "lucide-react";
import { ChecklistItemProgress } from "@/components/checklist/ChecklistItemProgress";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useChecklist, useSetCheck } from "@/hooks/use-checklist";
import { formatDate } from "@/lib/format";
import type { ChecklistItem } from "@/schemas/checklist";
import type { ProjectDetail } from "@/schemas/photos";

function CheckedNote({ item }: { item: ChecklistItem }) {
  if (!item.checked) return null;
  const note =
    item.checkedBy && item.checkedAt
      ? `Checked by ${item.checkedBy} on ${formatDate(item.checkedAt)}`
      : null;
  if (!note) return null;
  return (
    <span data-testid="checked-note" className="text-xs text-muted-foreground">
      {note}
    </span>
  );
}

function ItemRow({
  item,
  canEdit,
  onOpenPhotos,
}: {
  item: ChecklistItem;
  canEdit: boolean;
  onOpenPhotos: (photoId: string) => void;
}) {
  const setCheck = useSetCheck();
  return (
    <li data-testid="checklist-item" data-checked={item.checked} className="space-y-2 py-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{item.name}</p>
          {item.guidance !== item.name && (
            <p className="text-sm text-muted-foreground">{item.guidance}</p>
          )}
        </div>
        <ChecklistItemProgress
          requiredCount={item.requiredCount}
          assignedCount={item.assignedCount}
        />
        {canEdit ? (
          <Button
            variant={item.checked ? "default" : "outline"}
            size="sm"
            aria-pressed={item.checked}
            data-testid="check-item"
            disabled={setCheck.isPending}
            onClick={() =>
              setCheck.mutate({ itemId: item.id, request: { checked: !item.checked } })
            }
          >
            <Check className="size-4" />
            {item.checked ? "Checked" : "Check off"}
          </Button>
        ) : (
          <Badge variant={item.checked ? "default" : "outline"} data-testid="item-status">
            {item.checked ? "Checked" : "Not checked yet"}
          </Badge>
        )}
      </div>
      <CheckedNote item={item} />
      {item.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.photos.map((photo) => (
            <Button
              key={photo.id}
              variant="outline"
              size="sm"
              data-testid="item-photo"
              onClick={() => onOpenPhotos(photo.id)}
            >
              {photo.fileName}
            </Button>
          ))}
        </div>
      )}
    </li>
  );
}

/** The MC checklist of each album that has one. A PM checks items; an IE sees status. */
export function ChecklistTab({
  project,
  canEdit,
  onOpenPhoto,
}: {
  project: ProjectDetail;
  canEdit: boolean;
  /** Open a photo in the viewer, in the album it sits in. */
  onOpenPhoto: (albumId: string, photoId: string) => void;
}) {
  const { data, isPending, isError, refetch } = useChecklist(project.id);

  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (isError)
    return <ErrorState message="Could not load the checklist" onRetry={() => refetch()} />;
  if (data.albums.length === 0) {
    return (
      <EmptyState
        icon={ListChecks}
        title="No checklist on this project"
        className="rounded-lg border border-dashed border-border py-16"
      />
    );
  }
  return (
    <div className="space-y-6" data-testid="checklist-tab">
      <p className="text-sm text-muted-foreground">
        Items are MESH 1's own album help text; the one photo each needs is our default, to be
        confirmed with Madison.{" "}
        {canEdit
          ? "It is a guide: you can check an item whatever the count, and nothing is held up by it."
          : "It shows what has been shared with your firm."}
      </p>
      {data.albums.map((album) => (
        <section key={album.albumId} className="rounded-lg border" data-testid="checklist-album">
          <h3 className="border-b px-4 py-3 font-medium">{album.albumName}</h3>
          <ul className="divide-y px-4">
            {album.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                canEdit={canEdit}
                onOpenPhotos={(photoId) => onOpenPhoto(album.albumId, photoId)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
